import React, { useState } from 'react';
import { Idea } from '../services/api';

interface IdeaListProps {
  ideas: Idea[];
  onIdeaClick: (idea: Idea) => void;
  searchQuery?: string;
  totalCount?: number;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoading?: boolean;
}

export const IdeaList: React.FC<IdeaListProps> = ({ 
  ideas, 
  onIdeaClick, 
  searchQuery, 
  totalCount,
  hasMore,
  onLoadMore,
  isLoading
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getPriorityColor = (priority?: number) => {
    if (!priority) return '';
    if (priority >= 8) return 'priority-high';
    if (priority >= 5) return 'priority-medium';
    return 'priority-low';
  };

  const getStatusIcon = (idea: Idea) => {
    if (idea.transcription_status === 'pending') {
      return <span className="status-icon transcribing">🎤 Transcribing...</span>;
    }
    if (idea.transcription_status === 'processing') {
      return <span className="status-icon processing">⚙️ Processing...</span>;
    }
    if (idea.transcription_status === 'failed') {
      return <span className="status-icon failed">❌ Transcription failed</span>;
    }
    if (idea.processed) {
      return <span className="status-icon processed">✓ Processed</span>;
    }
    return null;
  };

  const highlightText = (text: string, query?: string) => {
    if (!query || !text) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <>
        {parts.map((part, index) => 
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={index} className="highlight">{part}</mark>
          ) : (
            part
          )
        )}
      </>
    );
  };

  return (
    <div className="idea-list">
      {ideas.length === 0 ? (
        <div className="empty-state">
          <p>No ideas yet. Start capturing your thoughts!</p>
        </div>
      ) : (
        ideas.map((idea) => (
          <div
            key={idea.id}
            className={`idea-item ${getPriorityColor(idea.priority)}`}
            onClick={() => onIdeaClick(idea)}
          >
            <div className="idea-header">
              <span className="idea-date">{formatDate(idea.created_at)}</span>
              {getStatusIcon(idea)}
              {idea.category && <span className="idea-category">{idea.category}</span>}
            </div>
            <div className="idea-content">
              {highlightText(idea.summary || idea.content, searchQuery)}
            </div>
            {idea.tags && (
              <div className="idea-tags">
                {JSON.parse(idea.tags).map((tag: string, index: number) => (
                  <span key={index} className="tag">#{tag}</span>
                ))}
              </div>
            )}
            {idea.audio_path && (
              <div className="idea-audio-indicator">
                🎙️ Voice note
              </div>
            )}
          </div>
        ))
      )}
      
      {hasMore && onLoadMore && (
        <div className="load-more-container">
          <button 
            className="load-more-button" 
            onClick={onLoadMore}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};